import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {FormulaValueCoercionUtils} from '@mintdata-wsp/common/controllers/spreadsheet/formula/FormulaValueCoercionUtils';
import {IFunctionContainer} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/ClientFunction';
import {Function, Param} from '@mintdata/mint-kit/api/decorators/Function';
import {
    ISheetAPI,
    ISheetLocation,
    SheetType,
    SheetValueType,
    SimpleNamingContext
} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {AInteger} from '@mintdata/mint-kit/api/values/AInteger';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {FrameField} from '@mintdata/mint-kit/api/values/frames/FrameField';
import {Frames} from '@mintdata/mint-kit/api/values/frames/Frames';
import {CoercionUtils} from '@mintdata/mint-kit/impl/coercionUtils/CoercionUtils';
import bind from 'bind-decorator';

type CacheOp<T> = (cacheName:string) => Promise<T | AError>;
type CacheKeyOp<T> = (cacheName:string, cacheKey:string) => Promise<T | AError>;

export class CacheFunctions implements IFunctionContainer
{
    public get namespace()
    {
        return 'cache';
    }

    @Function({
        title: 'CACHE(cacheName, cacheKey, cacheValue)',
        tip: 'If cacheValue points to a lazy-cell, it will be evaluated and then it\'s value will be put in a cache.' +
            'Uses old value on subsequent calls with the same key.‌',
        alias: 'CACHE',
        example: 'CACHE("myCache", "key1", A1)',
        icon: '',
        returns:
            {
                title: 'value',
                tip: 'The cached value.',
                type: SheetType.AnyIValue
            }
    })
    @bind
    public cache(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'cacheName',
            tip: 'Name of the cache to use.',
            type: Type.String
        }) cacheName:AString,
        @Param({
            title: 'cacheKey',
            tip: 'Key to cache on.',
            type: SheetType.AnyIValue
        }) cacheKey:IValue,
        @Param({
            title: 'cacheValue',
            tip: 'Cache value',
            type: SheetType.Any
        }) value:any):Promise<IValue | AError>
    {
        return this._performCacheKeyOp<IValue>(cacheName, cacheKey, (async (sCacheName, sCacheKey) =>
        {
            let cacheValue = await commonControllers.cache.getCacheValue(sCacheName, sCacheKey);
            if( !Type.isInstanceOf(cacheValue, Type.Empty) )
                return cacheValue;

            let newValue;

            if( Type.isInstanceOfIValue(value) )
                newValue = value as IValue;
            else if( SheetValueType.isInstanceOf(value, SheetType.SheetLocation) )
            {
                const cellReference:ISheetLocation = value as ISheetLocation;

                const ctx = api.getSheetContext();
                await ctx.reEvaluateLazyCell(cellReference);
                newValue = ctx.getCellValue(cellReference);
            }
            else if( SheetValueType.isInstanceOf(value, SheetType.SheetRange) )
                newValue = FormulaValueCoercionUtils.SheetRangeToIValue(api.getSheetContext(), new SimpleNamingContext(), value);
            else
                return AError.fromString('#INVALID_VALUE');

            // don't cache if it's an error
            if( Type.isInstanceOf(newValue, Type.Error) )
                return AError.fromString('#INVALID_VALUE');

            //don't cache empty values (but still return this empty value)
            if( Type.isInstanceOf(newValue, Type.Empty) )
                return newValue;

            //finally set new value to cache
            await commonControllers.cache.setCacheValue(sCacheName, sCacheKey, newValue);

            //...and then return it
            return newValue;
        }));
    }

    @Function({
        title: 'CACHE_SET(cacheName, cacheKey, cacheValue)',
        tip: 'Puts the value into cache.‌',
        alias: 'CACHE_SET',
        example: 'CACHE_SET("myCache", "key1", A1)',
        icon: '',
        returns:
            {
                title: 'value',
                tip: 'Returns the value, that was passed to it.',
                type: SheetType.AnyIValue
            }
    })
    @bind
    public cacheSet(
        @Param({
            title: 'cacheName',
            tip: 'Name of the cache to use.',
            type: Type.String
        }) cacheName:AString,
        @Param({
            title: 'cacheKey',
            tip: 'Key to cache on.',
            type: SheetType.AnyIValue
        }) cacheKey:IValue,
        @Param({
            title: 'cacheValue',
            tip: 'Value to store in cache.',
            type: SheetType.AnyIValue
        }) cacheValue:IValue):Promise<ABoolean | AError>
    {
        return this._performCacheKeyOp(cacheName, cacheKey, (sCacheName, sCacheKey) =>
            commonControllers.cache.setCacheValue(sCacheName, sCacheKey, cacheValue)
        );
    }

    @Function({
        title: 'CACHE_GET(cacheName, cacheKey)',
        tip: 'Gets the value from cache.‌',
        alias: 'CACHE_GET',
        example: 'CACHE_GET("myCache", "key1")',
        icon: '',
        returns:
            {
                title: 'value',
                tip: 'Returns the value from cache.',
                type: SheetType.AnyIValue
            }
    })
    @bind
    public cacheGet(
        @Param({
            title: 'cacheName',
            tip: 'Name of the cache to use.',
            type: Type.String
        }) cacheName:AString,
        @Param({
            title: 'cacheKey',
            tip: 'Key to cache on.',
            type: SheetType.AnyIValue
        }) cacheKey:IValue):Promise<IValue | AError>
    {
        return this._performCacheKeyOp(cacheName, cacheKey, (sCacheName, sCacheKey) =>
            commonControllers.cache.getCacheValue(sCacheName, sCacheKey)
        );
    }

    @Function({
        title: 'CACHE_CLEAR(cacheName, cacheKey)',
        tip: 'Removes the value from cache.‌',
        alias: 'CACHE_CLEAR',
        example: 'CACHE_CLEAR("myCache", "key1")',
        icon: '',
        returns:
            {
                title: 'value',
                tip: 'Returns true, if operation was successfull.',
                type: Type.Boolean
            }
    })
    @bind
    public cacheClear(
        @Param({
            title: 'cacheName',
            tip: 'Name of the cache to use.',
            type: Type.String
        }) cacheName:AString,
        @Param({
            title: 'cacheKey',
            tip: 'Key to cache on.',
            type: SheetType.AnyIValue
        }) cacheKey:IValue):Promise<ABoolean | AError>
    {
        return this._performCacheKeyOp(cacheName, cacheKey, (sCacheName, sCacheKey) =>
            commonControllers.cache.clearCacheValue(sCacheName, sCacheKey)
        );
    }

    @Function({
        title: 'DELETE_CACHE(cacheName)',
        tip: 'Deletes cache.‌',
        alias: 'DELETE_CACHE',
        example: 'DELETE_CACHE("myCache")',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'value',
                tip: 'Returns true if cache existed, false - if not.',
                type: Type.Boolean
            }
    })
    @bind
    public deleteCache(
        @Param({
            title: 'cacheName',
            tip: 'Name of the cache to use.',
            type: Type.String
        }) cacheName:AString):Promise<ABoolean | AError>
    {
        return this._performCacheOp(cacheName, sCacheName =>
            commonControllers.cache.deleteCache(sCacheName).then(result => new ABoolean(result))
        );
    }

    @Function({
        title: 'CACHES()',
        tip: 'Returns information about existing caches.‌',
        alias: 'CACHES',
        example: 'CACHES()',
        icon: '',
        returns:
            {
                title: 'frame',
                tip: 'Returns frame with existing cache informations.',
                type: Type.Frame
            }
    })
    @bind
    public caches():Promise<IFrame | AError>
    {
        return commonControllers.cache.listCaches().then((cacheInfos) =>
        {
            const fields = [
                new FrameField('name', Type.String),
                new FrameField('kind', Type.String),
                new FrameField('key_count', Type.Integer)
            ];

            const data = cacheInfos.map(info => [
                new AString(info.name),
                new AString(info.kind),
                new AInteger(info.keyCount)
            ]);

            return Frames.fromFieldsAndData(fields, data);
        });
    }

    @Function({
        title: 'CACHE_KEYS(cacheName, [maxRows])',
        tip: 'Returns all the keys, currently stored in cache.‌',
        alias: 'CACHE_KEYS',
        example: 'CACHE_KEYS("myCache")',
        icon: '',
        returns:
            {
                title: 'frame',
                tip: 'Returns frame with all the keys.',
                type: Type.Frame
            }
    })
    @bind
    public cacheKeys(
        @Param({
            title: 'cacheName',
            tip: 'Name of the cache to use.',
            type: Type.String
        }) cacheName:AString,
        @Param({
            title: 'maxRows',
            tip: 'Limit returned rows count.',
            type: Type.Integer,
            optional: true
        }) maxRows:AInteger):Promise<IFrame | AError>
    {
        return this._performCacheOp(cacheName, sCacheName =>
            commonControllers.cache
                .getCacheKeys(sCacheName, maxRows ? maxRows.value : null)
                .then((keys) =>
                {
                    const fields = [
                        new FrameField('key', Type.String)
                    ];

                    const data = keys.map(key => [new AString(key)]);

                    return Frames.fromFieldsAndData(fields, data);
                })
        );
    }

    @Function({
        title: 'CACHE_ENTRIES(cacheName, [maxRows])',
        tip: 'Returns all the entries, currently stored in cache.‌',
        alias: 'CACHE_ENTRIES',
        example: 'CACHE_ENTRIES("myCache")',
        icon: '',
        returns:
            {
                title: 'frame',
                tip: 'Returns frame with all the entries.',
                type: Type.Frame
            }
    })
    @bind
    public cacheEntries(
        @Param({
            title: 'cacheName',
            tip: 'Name of the cache to use.',
            type: Type.String
        }) cacheName:AString,
        @Param({
            title: 'maxRows',
            tip: 'Limit returned rows count.',
            type: Type.Integer,
            optional: true
        }) maxRows:AInteger):Promise<IFrame | AError>
    {
        return this._performCacheOp(cacheName, sCacheName =>
            commonControllers.cache
                .getCacheEntries(sCacheName, maxRows ? maxRows.value : null)
                .then((entries) =>
                {
                    const fields = [
                        new FrameField('key', Type.String),
                        new FrameField('value', Type.String)
                    ];

                    const data = entries.map(({key, value}) => [new AString(key), value]);

                    return Frames.fromFieldsAndData(fields, data);
                })
        );
    }

    private _performCacheKeyOp<T>(cacheName:AString, cacheKey:IValue, op:CacheKeyOp<T>):Promise<T | AError>
    {
        return this._performCacheOp(cacheName, sCacheName =>
        {
            if( Type.isInstanceOf(cacheKey, Type.Frame) )
                return AError.fromString('#CACHE_KEY_NOT_PRIMITIVE');

            const coercedKey:IValue = CoercionUtils.coerce(cacheKey, Type.String, null);
            if( Type.isInstanceOf(coercedKey, Type.Error) )
                return coercedKey as AError;

            const key = (coercedKey as AString).value;

            return op.call(null, sCacheName, key);
        });
    }

    private async _performCacheOp<T>(cacheName:AString, op:CacheOp<T>):Promise<T | AError>
    {
        return Promise.resolve().then(() =>
        {
            if( !cacheName || !cacheName.value.match(/^[A-Za-z0-9-_]*$/) )
                return AError.fromString('#INVALID_CACHE_NAME');

            const sCacheName = cacheName.value.toLowerCase();
            return op.call(null, sCacheName);
        });
    }
}
